// @flow

import * as React from 'react';
import { connect } from 'react-redux';

import trackEventAJAX, { ThirdPartyTracking } from '@ta/common.tracking';
import { NavigableListItem } from '@ta/list.navigable-list';
import { localize } from '@ta/common.i18n';
import type { ViewportCategory } from '@ta/common.state';
import type { PaddingStyle } from './BasicResult';
import type { FetcherResult } from '../Typeahead';
import TypeaheadTracking, { type UserInteractionType } from '../TypeaheadTracking';
import type { TypeaheadResult } from '../TypeaheadResult';
import type { AddressDisplay } from './addressDisplay';
import formatAddress from './addressDisplay';

/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['noPadding', 'narrow']}] */
import styles from './SponsoredGeoResult.less';

type Props = {|
  /** The RecentlyViewedFetcher result */
  result: FetcherResult,

  /** The typeahead tracker */
  tracker: TypeaheadTracking,

  /** An optional callback if a result is selected */
  onSelection?: (
    event: SyntheticEvent<HTMLElement> | KeyboardEvent,
    result: TypeaheadResult,
  ) => void,

  /** An optional callback for if/when the mouse moves over the result */
  onMouseMove?: (event: SyntheticMouseEvent<HTMLElement>) => mixed,

  /** True if this result is active. Defaults to false */
  active?: boolean,

  /** Padding style for the result */
  paddingStyle?: PaddingStyle,

  /** Display mode for address subtitle of results.
   * If no display mode is provided, there will be no address displayed in subtitle */
  addressDisplay: AddressDisplay,

  /** The number of other items in the results list */
  numListItems: number,

  /** The device type: mobile, tablet, or desktop. Calculated via redux. */
  viewportCategory: ViewportCategory,

  /** The servlet name. Calculated via redux. */
  servletName: string | null,

  /** result position in result list */
  resultIndex?: number,
|};

type State = {|
  thirdPartyClickTrackerURL?: string,
|};

/**
 * Create a sponsored geo search result. This is currently in test, and only returns one of several hard-corded
 * results from the assocaited fetcher.
 * todo: if this get past the test, add .story and .test files.
 *
 * @author mholland
 * @since Jun 2019
 */
class SponsoredGeoResult extends React.Component<Props, State> {
  static defaultProps = {
    onSelection: undefined,
    onMouseMove: undefined,
    active: false,
    paddingStyle: undefined,
    addressDisplay: 'parent_geos',
    numListItems: -1,
    servletName: 'servlet_unknown',
    resultIndex: undefined,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      thirdPartyClickTrackerURL: '',
    };
  }

  onSelect = (event: SyntheticEvent<HTMLElement> | KeyboardEvent) => {
    const userInteractionType: UserInteractionType = event.type === 'click' ? 'click' : 'enter';
    const { tracker, onSelection, result, resultIndex } = this.props;
    const { location_id: geoId, dfpTrackingUrlTemplate = null, thirdPartyClickTrackerURL } = result;

    // perform both standard tracking, as well as supplemental sponsorship tracking with more info
    this.trackWhereToGARecordEvnt(userInteractionType);

    const trackingData = {
      resultIndex,
      type: 'GEO',
      data_type: 'LOCATION',
      document_id: `sponsored_${geoId}`,
      ...result,
    };

    tracker.trackSelection(trackingData, userInteractionType);

    if (onSelection) {
      this.setState({ thirdPartyClickTrackerURL });
      onSelection(event, {
        type: 'recent_location_id',
        value: geoId,
        dfpTrackingUrlTemplate,
      });
    }
  };

  /**
   * Tracks a GARecord event, tailored to the needs of sponsored whereTo tracking.
   * @param action
   */
  trackWhereToGARecordEvnt = (action: string) => {
    const context = [
      this.props.servletName,
      this.props.viewportCategory,
      this.props.result.location_id,
      'top',
      this.props.numListItems,
    ].join('|');
    trackEventAJAX({
      module: 'SponsoredWhereTo',
      action,
      context,
      nonInteractive: false,
      value: 0,
    });
  };

  render() {
    const { result } = this.props;
    const { name: title, ancestors = [] } = result;

    const subtitle = formatAddress(
      this.props.addressDisplay,
      ancestors[0]?.name,
      ancestors[1]?.name,
      // eslint-disable-next-line camelcase
      result?.address_obj?.street1,
    );

    const className = [styles.result];
    if (this.props.active) {
      className.push(styles.active);
    }
    if (this.props.paddingStyle) {
      className.push(styles[this.props.paddingStyle]);
    }
    const subElem = subtitle ? <span className={styles.subtitle}>{subtitle}</span> : undefined;
    const sponsoredPill = (
      <span className={styles.sponsoredPill}>{localize('social_mobile_reason_for_sponsored')}</span>
    );

    return (
      <React.Fragment>
        <ThirdPartyTracking url={this.state.thirdPartyClickTrackerURL} />
        <NavigableListItem
          active={!!this.props.active}
          onMouseMove={this.props.onMouseMove}
          onSelection={this.onSelect}
        >
          <div className={className.join(' ')}>
            <span className={styles.resultTitle}>
              {title}
              {sponsoredPill}
              {subElem}
            </span>
          </div>
        </NavigableListItem>
      </React.Fragment>
    );
  }
}

// $FlowFixMe92
export default connect(state => ({
  viewportCategory: state.meta.device.viewportCategory,
  servletName: state.meta.initialServletName,
}))(SponsoredGeoResult);
